import React, { useCallback, useState } from "react";
import ReactTooltip from "react-tooltip";

import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { UpdateInvestor } from "components";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { InvestorWithBank } from "store/types";
import { toastify } from "utils/tyostify";

import { useActionHook, useModals } from "../../hooks";
import { IApiServiceError, useAccountSelector, useDealActions, useInvestorsSelector } from "../../store";

import "./InvestorsTable.styles.scss";

export interface InvestorsTableProps {
  searchValue: string;
  className?: string;
}

export const InvestorsTable: React.FC<InvestorsTableProps> = ({ className, searchValue }) => {
  const investors = useInvestorsSelector();
  const [investor, setInvestor] = useState<InvestorWithBank | null>(null);
  const { deleteModal, errorModal } = useModals();
  const { deleteInvestorAction, inviteInvestorAction } = useDealActions();
  const { support_investor_groups } = useAccountSelector() || {};

  const headerTitles: string[] = [
    "Investor Name",
    ...(support_investor_groups ? ["Group"] : []),
    "Email Address",
    "Phone Number",
    "Company",
    ""
  ];

  const handleDelete = useCallback((id: number) => {
    deleteModal({
      action: async () => {
        await deleteInvestorAction(id);
      },
      message: "Are you sure you want to archive investor?",
      element: "Archive Investor",
      onPressSuccess: "Archive"
    });
  }, []);

  const { action: inviteInvestor } = useActionHook(inviteInvestorAction, {
    onSuccess: () => toastify({ message: "Invite has been sent to investor successfully" }),
    onReject: (error) => errorModal({ msg: (error as IApiServiceError).message || "Something went wrong" })
  });

  const filteredInvestors = investors.filter((investor) =>
    investor.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <div className="investors-table__wrapper">
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "8px",
          boxShadow: "none",
          height: "100%",
          border: "1px solid #dae0e2",
          borderTop: "none"
        }}
      >
        <Table
          className={`${className} investors-table`}
          sx={{
            ".MuiTableCell-root": {
              padding: "14px 26px",
              ":last-child": { width: "4%" },
              ":first-child": { minWidth: "200px", maxWidth: "200px", padding: "14px 0px 14px 17px" }
            }
          }}
          stickyHeader
        >
          <TableHead className="investors-table__head">
            <TableRow className="investors-table__head-row">
              {headerTitles.map((item: string, index: number) => (
                <TableCell className="investors-table__head-row-cell" key={index}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className="investors-table__body">
            {filteredInvestors.map((item) => {
              const { id, name, email, phone_number, company, group } = item;
              return (
                <TableRow className="investors-table__body-row" key={id}>
                  <TableCell
                    className="investors-table__body-row-cell"
                    onClick={() => setInvestor(item)}
                    data-tip={name?.length > 30 ? name : ""}
                  >
                    {name}
                  </TableCell>
                  {support_investor_groups && (
                    <TableCell
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "200px !important"
                      }}
                      className="investors-table__body-row-cell"
                      data-tip={group && group?.length > 30 ? group : ""}
                    >
                      {group}
                    </TableCell>
                  )}
                  <TableCell className="investors-table__body-row-cell" data-tip={email?.length > 30 ? email : ""}>
                    {email}
                  </TableCell>
                  <TableCell className="investors-table__body-row-cell">{phone_number}</TableCell>
                  <TableCell className="investors-table__body-row-cell">{company}</TableCell>
                  <TableCell className="investors-table__body-row-cell">
                    <Stack flexDirection="row" justifyContent="center" alignItems="center" gap="10px">
                      <MaterialTooltip isShow title="Delete investor">
                        <IconButton className="investors-table__body-row-cell-archive" onClick={() => handleDelete(id)}>
                          <ArchiveOutlinedIcon sx={{ height: 18, width: 18, fill: "#173c4c" }} />
                        </IconButton>
                      </MaterialTooltip>
                      <MaterialTooltip isShow title="Invite to Investor Report">
                        <IconButton onClick={() => inviteInvestor(id)}>
                          <ForwardToInboxOutlinedIcon />
                        </IconButton>
                      </MaterialTooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ReactTooltip className="tooltip" place="top" effect="solid" type="light" />
      <UpdateInvestor investor={investor} onClose={() => setInvestor(null)} />
    </div>
  );
};
